import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useLayoutEffect,
  useCallback,
} from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import googleCalendarPlugin from "@fullcalendar/google-calendar";
import momentTimezonePlugin from "@fullcalendar/moment-timezone";
import Breadcrumbs from "../../components/shared/Breadcrumbs";
import SubPageLayout from "../../components/shared/SubPageLayout";
import { GoogleMap, LoadScript } from "@react-google-maps/api";
import moment from "moment-timezone";
import MiniCalendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { apiConfig } from "../../utils/apiConfig";
import {
  useEvents,
  EVENT_TYPES,
  EVENT_STATUS,
} from "../../contexts/EventContext";
import { useLocation } from "react-router-dom";
import EventModal from "../../components/shared/calendar/EventModal";

import logger from '../../utils/logger';

// Add color options constant
const colorOptions = [
  { color: "#039be5", label: "Blue" },
  { color: "#33b679", label: "Green" },
  { color: "#f6bf26", label: "Yellow" },
  { color: "#e67c73", label: "Red" },
  { color: "#8e24aa", label: "Purple" },
  { color: "#616161", label: "Gray" },
  { color: "#7986cb", label: "Lavender" },
  { color: "#f4511e", label: "Orange" },
];

// FullCalendar custom styles
const fullCalendarStyles = {
  ".fc": {
    height: "100%",
    fontFamily: '"Google Sans", Roboto, Arial, sans-serif',
    "--fc-border-color": "#dadce0",
    "--fc-today-bg-color": "#e8f0fe",
    "--fc-neutral-bg-color": "#ffffff",
    "--fc-list-event-hover-bg-color": "#f8f9fa",
  },
  ".fc .fc-toolbar-title": {
    fontSize: "22px",
    fontWeight: "400",
    color: "#3c4043",
    padding: "8px 12px",
    lineHeight: "28px",
  },
  ".fc .fc-button": {
    backgroundColor: "transparent",
    border: "1px solid #dadce0",
    color: "#3c4043",
    textTransform: "none",
    boxShadow: "none",
    padding: "8px 16px",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: "500",
    transition: "all 0.2s ease",
  },
  ".fc .fc-button:hover": {
    backgroundColor: "#f1f3f4",
    borderColor: "#dadce0",
  },
  ".fc .fc-button-primary:not(:disabled).fc-button-active": {
    backgroundColor: "#e8f0fe",
    borderColor: "#dadce0",
    color: "#1a73e8",
  },
  ".fc .fc-event": {
    borderRadius: "4px",
    border: "none",
    fontSize: "12px",
    padding: "2px 4px",
    margin: "1px 0",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
  },
  ".fc .fc-event:hover": {
    filter: "brightness(0.95)",
  },
  ".fc .fc-col-header-cell-cushion": {
    color: "#3c4043",
    fontWeight: "500",
    padding: "8px",
    fontSize: "14px",
  },
  ".fc .fc-daygrid-day-number": {
    color: "#3c4043",
    padding: "8px",
    fontSize: "12px",
  },
  ".fc .fc-timegrid-slot-label-cushion": {
    color: "#70757a",
    fontSize: "12px",
  },
  ".fc-theme-standard td, .fc-theme-standard th": {
    border: "1px solid #dadce0",
  },
  ".fc-scrollgrid-section-header th": {
    background: "#ffffff",
    borderBottom: "1px solid #dadce0",
  },
  ".fc-day-today": {
    background: "#e8f0fe !important",
  },
  ".fc-timegrid-now-indicator-line": {
    borderColor: "#ea4335",
  },
  ".fc-timegrid-now-indicator-arrow": {
    borderColor: "#ea4335",
  },
  ".fc-toolbar": {
    padding: "12px",
    borderBottom: "1px solid #dadce0",
    marginBottom: "0 !important",
  },
  ".fc-view-harness": {
    background: "#ffffff",
  },
  ".fc .fc-timegrid-slot": {
    height: "48px",
  },
  ".fc .fc-timegrid-axis": {
    padding: "0 8px",
  },
};

const calendarStyles = {
  ":root": {
    "--fc-button-text-color": "#1a1a1a",
    "--fc-button-bg-color": "#ffffff",
    "--fc-button-border-color": "#e5e7eb",
    "--fc-button-hover-bg-color": "#f3f4f6",
    "--fc-button-hover-border-color": "#d1d5db",
    "--fc-button-active-bg-color": "#e5e7eb",
    "--fc-button-active-border-color": "#9ca3af",

    "--fc-event-bg-color": "#e6f4ea",
    "--fc-event-border-color": "#029831",
    "--fc-event-text-color": "#1a1a1a",
    "--fc-event-selected-overlay-color": "rgba(0, 0, 0, 0.25)",

    "--fc-more-link-bg-color": "#ffffff",
    "--fc-more-link-text-color": "#1a1a1a",

    "--fc-daygrid-event-dot-width": "8px",

    "--fc-today-bg-color": "#e6f4ea",
    "--fc-highlight-color": "rgba(188, 232, 241, 0.3)",

    "--fc-non-business-color": "rgba(215, 215, 215, 0.3)",
    "--fc-bg-event-color": "rgb(143, 223, 130)",
    "--fc-bg-event-opacity": "0.3",

    "--fc-daygrid-day-number-color": "#1a1a1a",
    "--fc-daygrid-day-today-bg-color": "#e6f4ea",

    "--fc-list-event-dot-width": "10px",
    "--fc-list-event-hover-bg-color": "#f3f4f6",

    "--fc-page-bg-color": "#ffffff",
    "--fc-neutral-bg-color": "#ffffff",
    "--fc-neutral-text-color": "#1a1a1a",
    "--fc-border-color": "#e5e7eb",
  },

  ".fc": {
    fontFamily:
      'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },

  ".fc .fc-toolbar-title": {
    color: "#1a1a1a",
    fontSize: "1.25rem",
    fontWeight: "600",
  },

  ".fc .fc-button": {
    fontWeight: "500",
    fontSize: "0.875rem",
    padding: "0.5rem 1rem",
    borderRadius: "0.375rem",
    transition: "all 0.2s",
  },

  ".fc .fc-button:focus": {
    boxShadow: "0 0 0 2px rgba(2, 152, 49, 0.5)",
    outline: "none",
  },

  ".fc .fc-daygrid-day-number": {
    color: "#1a1a1a",
    fontWeight: "500",
    padding: "0.5rem",
  },

  ".fc .fc-daygrid-day.fc-day-today": {
    backgroundColor: "#e6f4ea !important",
  },

  ".fc .fc-daygrid-day-top": {
    justifyContent: "center",
    paddingTop: "0.25rem",
  },

  ".fc .fc-list-day-cushion": {
    backgroundColor: "#f9fafb !important",
  },

  ".fc .fc-timegrid-slot-label": {
    color: "#1a1a1a",
    fontWeight: "500",
  },

  ".fc .fc-timegrid-axis-cushion": {
    color: "#1a1a1a",
    fontWeight: "500",
  },

  ".fc .fc-col-header-cell-cushion": {
    color: "#1a1a1a",
    fontWeight: "600",
  },

  ".fc-theme-standard .fc-list-day-cushion": {
    backgroundColor: "#f9fafb",
  },

  ".fc-direction-ltr .fc-daygrid-event.fc-event-end": {
    marginRight: "0",
  },
};

// Add styles to the document
const styleSheet = document.createElement("style");
styleSheet.textContent = Object.entries(calendarStyles)
  .map(
    ([selector, styles]) =>
      `${selector} { ${Object.entries(styles)
        .map(([prop, value]) =>
          typeof value === "object"
            ? `${selector}${prop} { ${Object.entries(value)
                .map(([subProp, subValue]) => `${subProp}: ${subValue}`)
                .join(";")} }`
            : `${prop}: ${value}`,
        )
        .join(";")} }`,
  )
  .join("\n");
document.head.appendChild(styleSheet);

// Update the mini calendar styles
const miniCalendarStyles = `
  .react-calendar {
    width: 100%;
    border: none;
    font-family: inherit;
    line-height: 1.125em;
    background: white;
  }
  
  .react-calendar__navigation {
    height: 24px;
    margin-bottom: 0.5em;
  }
  
  .react-calendar__navigation button {
    min-width: 24px;
    padding: 0;
    font-size: 0.8rem;
    color: #1a1a1a !important;
    background: none !important;
  }
  
  .react-calendar__navigation button:disabled {
    color: #999999 !important;
  }
  
  .react-calendar__navigation button:enabled:hover,
  .react-calendar__navigation button:enabled:focus {
    background-color: #f3f4f6 !important;
  }
  
  .react-calendar__month-view__weekdays {
    font-size: 0.7rem;
    padding: 0;
    text-transform: uppercase;
    font-weight: 500;
    color: #1a1a1a;
  }
  
  .react-calendar__month-view__weekdays__weekday {
    padding: 0.2em;
    color: #1a1a1a;
  }
  
  .react-calendar__month-view__weekdays__weekday abbr {
    text-decoration: none;
    color: #1a1a1a;
  }
  
  .react-calendar__month-view__days__day {
    padding: 0.2em 0;
    font-size: 0.7rem;
    height: 24px;
    color: #1a1a1a !important;
  }
  
  .react-calendar__month-view__days__day--neighboringMonth {
    color: #757575 !important;
  }
  
  .react-calendar__tile {
    padding: 0;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1a1a1a !important;
    font-size: 0.7rem;
  }
  
  .react-calendar__tile--now {
    background: #e6f4ea !important;
    color: #1a1a1a !important;
  }
  
  .react-calendar__tile--active {
    background: #029831 !important;
    color: #1a1a1a !important;
  }
  
  .react-calendar__tile:enabled:hover,
  .react-calendar__tile:enabled:focus {
    background-color: #f3f4f6 !important;
    color: #1a1a1a !important;
  }
`;

// Move static styles and configs outside component
const DEFAULT_BUSINESS_HOURS = {
  daysOfWeek: [1, 2, 3, 4, 5],
  startTime: "09:00",
  endTime: "17:00",
};

const generateMockEvents = (start, end) => {
  const events = [];
  const eventTypes = Object.keys(EVENT_TYPES);
  const usedTimes = new Set(); // Track used time slots to prevent duplicates

  // Calculate the start date (3 weeks before current date)
  const currentDate = new Date();
  const startDate = new Date(currentDate);
  startDate.setDate(startDate.getDate() - 21); // 3 weeks before

  // Generate events for 7 weeks (49 days)
  for (let i = 0; i < 49; i++) {
    const currentDay = new Date(startDate);
    currentDay.setDate(startDate.getDate() + i);

    // Skip weekends
    if (currentDay.getDay() === 0 || currentDay.getDay() === 6) {
      continue;
    }

    // Generate 2-3 events per day (reduced from 4-6)
    const eventsPerDay = Math.floor(Math.random() * 2) + 2; // Random number between 2 and 3

    for (let j = 0; j < eventsPerDay; j++) {
      // Generate random start time between 9 AM and 4 PM
      let startHour = Math.floor(Math.random() * 8) + 9; // 9 AM to 4 PM
      let startMinute = Math.floor(Math.random() * 4) * 15; // 0, 15, 30, or 45 minutes

      // Create a unique time key
      const timeKey = `${currentDay.toISOString().split("T")[0]}-${startHour}-${startMinute}`;

      // Skip if this time slot is already used
      if (usedTimes.has(timeKey)) {
        continue;
      }

      usedTimes.add(timeKey);

      const start = new Date(currentDay);
      start.setHours(startHour, startMinute, 0, 0);

      const end = new Date(start);
      end.setHours(startHour + 1, startMinute, 0, 0);

      const eventTypeKey =
        eventTypes[Math.floor(Math.random() * eventTypes.length)];
      const eventTypeConfig = EVENT_TYPES[eventTypeKey];

      // Generate random attendees (1-3 people)
      const attendees = Array.from(
        { length: Math.floor(Math.random() * 3) + 1 },
        (_, index) => ({
          id: `attendee-${i}-${j}-${index}`,
          name: `Attendee ${index + 1}`,
          email: `attendee${index + 1}@example.com`,
          response: "accepted",
        }),
      );

      events.push({
        id: `event-${i}-${j}`,
        title: eventTypeConfig.label,
        start: start.toISOString(),
        end: end.toISOString(),
        backgroundColor: eventTypeConfig.color,
        borderColor: eventTypeConfig.borderColor,
        extendedProps: {
          type: eventTypeKey,
          location: "Office",
          description: `This is a ${eventTypeConfig.label.toLowerCase()}`,
          attendees: attendees,
        },
      });
    }
  }

  return events;
};

const Calendar = ({
  isDashboard = false,
  view = "timeGridWeek",
  onCreateEvent,
}) => {
  const {
    events,
    tasks,
    initializeEvents,
    updateEvent,
    updateTask,
    cleanEventTitle,
    getEventStatus,
  } = useEvents();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [modalMode, setModalMode] = useState("create");
  const calendarRef = useRef(null);
  const [showWeekends, setShowWeekends] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [calendarSectionsCollapsed, setCalendarSectionsCollapsed] = useState({
    myCalendars: false,
    otherCalendars: false,
  });
  const [eventTypeFilters, setEventTypeFilters] = useState({
    CLIENT: true,
    PROSPECT: true,
    INTERNAL: true,
    EXTERNAL: true,
    WORKSHOP: true,
    TASK: true,
  });
  const location = useLocation();

  // Get initial view from location state or default
  const initialView = useMemo(() => {
    if (location.state?.view) {
      return location.state.view;
    }
    return view;
  }, [location.state, view]);

  // Initialize events on component mount
  useEffect(() => {
    initializeEvents();
  }, [initializeEvents]);

  // Update calendar view when location state changes
  useEffect(() => {
    if (location.state?.view && calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.changeView(location.state.view);
    }
  }, [location.state]);

  // Debug the event types
  useEffect(() => {
    logger.info(
      "Events:",
      events.map((e) => ({ title: e.title, type: e.extendedProps?.type })),
    );
  }, [events]);

  // Add some mock events for missing types
  useEffect(() => {
    if (events.length > 0) {
      const now = new Date();
      const mockEvents = [
        {
          id: "mock-prospect-1",
          title: "Prospect Meeting - New Client",
          start: new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate() + 1,
            10,
            0,
          ),
          end: new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate() + 1,
            11,
            0,
          ),
          extendedProps: {
            type: "PROSPECT",
            attendees: [],
            status: EVENT_STATUS.PENDING,
          },
        },
        {
          id: "mock-external-1",
          title: "External Meeting - Investment Review",
          start: new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate() + 2,
            14,
            0,
          ),
          end: new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate() + 2,
            15,
            30,
          ),
          extendedProps: {
            type: "EXTERNAL",
            attendees: [],
            status: EVENT_STATUS.PENDING,
          },
        },
      ];

      // Add mock events to the events array
      events.push(...mockEvents);
    }
  }, [events]);

  // Handle filter change
  const handleFilterChange = (type) => {
    setEventTypeFilters((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  // Update the allEvents useMemo to handle the event types
  const allEvents = useMemo(() => {
    const taskEvents = tasks.map((task) => ({
      ...task,
      extendedProps: {
        ...task.extendedProps,
        type: "TASK",
      },
      classNames: [
        getEventStatus(task) === EVENT_STATUS.OVERDUE
          ? "task-overdue"
          : "task-pending",
      ],
    }));

    const mappedEvents = events.map((event) => {
      // Extract type from title if not explicitly set
      let eventType = event.extendedProps?.type;
      if (!eventType) {
        const title = event.title.toLowerCase();
        if (
          title.includes("portfolio review") ||
          title.includes("client meeting")
        ) {
          eventType = "CLIENT";
        } else if (title.includes("prospect")) {
          eventType = "PROSPECT";
        } else if (title.includes("internal") || title.includes("quick call")) {
          eventType = "INTERNAL";
        } else if (title.includes("external")) {
          eventType = "EXTERNAL";
        } else if (title.includes("training") || title.includes("workshop")) {
          eventType = "WORKSHOP";
        } else {
          eventType = "CLIENT"; // default fallback
        }
      }

      // Set colors based on event type
      const typeConfig = EVENT_TYPES[eventType];
      return {
        ...event,
        extendedProps: {
          ...event.extendedProps,
          type: eventType,
        },
        backgroundColor: typeConfig.color,
        borderColor: typeConfig.borderColor,
      };
    });

    const combinedEvents = [...mappedEvents, ...taskEvents];

    return combinedEvents.filter((event) => {
      const eventType = event.extendedProps?.type;
      return eventTypeFilters[eventType];
    });
  }, [events, tasks, getEventStatus, eventTypeFilters]);

  // Handle date select for event creation
  const handleDateSelect = (selectInfo) => {
    setSelectedEvent({
      title: "",
      start: selectInfo.start,
      end: selectInfo.end,
      allDay: selectInfo.allDay,
      type: "CLIENT",
      extendedProps: {
        attendees: [],
        virtualMeetingLink: null,
        status: EVENT_STATUS.PENDING,
      },
    });
    setModalMode("create");
    setIsModalOpen(true);
  };

  // Handle event click
  const handleEventClick = (clickInfo) => {
    const event = clickInfo.event;
    logger.info("Raw FullCalendar event:", {
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      allDay: event.allDay,
      extendedProps: event.extendedProps,
      _def: event._def,
      _instance: event._instance,
    });

    // Get the raw event data
    const eventData = {
      id: event.id,
      title: event._def.title, // Use _def to get the original title
      start: event._instance.range.start,
      end: event._instance.range.end,
      allDay: event._def.allDay,
      type: event._def.extendedProps?.type || "CLIENT",
      location: event._def.extendedProps?.location || "",
      description: event._def.extendedProps?.description || "",
      extendedProps: {
        attendees: event._def.extendedProps?.attendees || [],
        virtualMeetingLink: event._def.extendedProps?.virtualMeetingLink || "",
        status: event._def.extendedProps?.status || EVENT_STATUS.PENDING,
        location: event._def.extendedProps?.location || "",
        description: event._def.extendedProps?.description || "",
      },
    };

    logger.info("Processed event data for edit:", eventData);
    setSelectedEvent(eventData);
    setModalMode("edit");
    setIsModalOpen(true);
  };

  // Toggle calendar sections
  const toggleSection = (section) => {
    setCalendarSectionsCollapsed((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Handle create event button click
  const handleCreateEventClick = () => {
    const calendarApi = calendarRef.current.getApi();
    const now = calendarApi.getDate();

    // Round to the nearest hour
    const roundedNow = new Date(now);
    roundedNow.setMinutes(0);
    roundedNow.setSeconds(0);
    roundedNow.setMilliseconds(0);

    // If we're past the 30-minute mark, round up to the next hour
    if (now.getMinutes() >= 30) {
      roundedNow.setHours(roundedNow.getHours() + 1);
    }

    const roundedEnd = new Date(roundedNow);
    roundedEnd.setHours(roundedEnd.getHours() + 1);

    setSelectedEvent({
      title: "",
      start: roundedNow,
      end: roundedEnd,
      allDay: false,
      type: "CLIENT",
      backgroundColor: EVENT_TYPES.CLIENT.color,
      borderColor: EVENT_TYPES.CLIENT.borderColor,
      extendedProps: {
        type: "CLIENT",
        attendees: [],
        virtualMeetingLink: null,
        status: EVENT_STATUS.PENDING,
        location: "",
        description: "",
      },
    });
    setModalMode("create");
    setIsModalOpen(true);
  };

  // Handle modal submit
  const handleModalSubmit = (eventData) => {
    if (eventData.type === "TASK") {
      updateTask(eventData);
    } else {
      updateEvent(eventData);
    }
    setIsModalOpen(false);
  };

  // Custom event render
  const renderEvent = (eventInfo) => {
    const event = eventInfo.event;
    const isTask = event.extendedProps.type === "TASK";
    const status = getEventStatus(event);
    const attendeeCount = event.extendedProps.attendees?.length || 0;

    return (
      <div
        className={`
          event-content
          ${isTask ? "task-event" : ""}
          ${status === EVENT_STATUS.OVERDUE ? "overdue" : ""}
          ${status === EVENT_STATUS.COMPLETED ? "completed" : ""}
        `}
      >
        <div className="event-title">
          {cleanEventTitle(event.title)}
          {attendeeCount > 0 && (
            <span className="attendee-count">👥 {attendeeCount}</span>
          )}
        </div>
        {!isTask && (
          <div className="event-time">
            {moment(event.start).format("h:mm A")} -{" "}
            {moment(event.end).format("h:mm A")}
          </div>
        )}
      </div>
    );
  };

  // Render sidebar
  const renderSidebar = () => (
    <div
      className={`w-64 border-r border-gray-200 dark:border-gray-700 bg-surface-default dark:bg-surface-default h-full flex flex-col ${
        sidebarCollapsed ? "w-0 overflow-hidden" : ""
      }`}
    >
      {/* Mini Calendar */}
      <div className="border-b border-gray-200 dark:border-gray-700 p-4">
        <MiniCalendar
          onChange={(date) => {
            if (calendarRef.current) {
              const calendarApi = calendarRef.current.getApi();
              calendarApi.gotoDate(date);
            }
          }}
          value={new Date()}
          minDetail="month"
          maxDetail="month"
          navigationLabel={({ date }) =>
            date.toLocaleDateString("en-US", {
              month: "short",
              year: "2-digit",
            })
          }
          formatShortWeekday={(locale, date) =>
            date.toLocaleDateString("en-US", { weekday: "narrow" })
          }
          showFixedNumberOfWeeks={false}
          showNeighboringMonth={false}
          locale="en-US"
          className="w-full"
          tileClassName={({ date }) => {
            const today = new Date();
            return date.toDateString() === today.toDateString()
              ? "rounded-full bg-[#e6f4ea] text-[#029831]"
              : "";
          }}
        />
      </div>

      {/* Calendar Sections */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* My Calendars Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">My Calendars</h3>
            <button
              onClick={() => toggleSection("myCalendars")}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
            >
              {calendarSectionsCollapsed.myCalendars ? (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
              ) : (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 12H4"
                  />
                </svg>
              )}
            </button>
          </div>
          {!calendarSectionsCollapsed.myCalendars && (
            <div className="space-y-2">
              {Object.entries(EVENT_TYPES).map(([type, config]) => (
                <label
                  key={type}
                  className="flex items-center space-x-2 group cursor-pointer"
                >
                  <div className="relative flex items-center">
                    <input
                      type="checkbox"
                      checked={eventTypeFilters[type]}
                      onChange={() => handleFilterChange(type)}
                      className="peer sr-only"
                    />
                    <div
                      className={`w-4 h-4 border-2 rounded flex items-center justify-center transition-colors
                        ${eventTypeFilters[type] ? "bg-current border-current" : "border-gray-300 bg-surface-default dark:bg-surface-default"}`}
                      style={{ color: config.color }}
                    >
                      {eventTypeFilters[type] && (
                        <svg
                          className="w-3 h-3 text-white"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <path
                            d="M10 3L4.5 8.5L2 6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between flex-1">
                    <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:text-gray-100">
                      {config.label}
                    </span>
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: config.color }}
                    />
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Display Options */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
            Display Options
          </h3>
          <div className="space-y-2">
            <label className="flex items-center space-x-2 cursor-pointer group">
              <div className="relative flex items-center">
                <input
                  type="checkbox"
                  checked={showWeekends}
                  onChange={(e) => setShowWeekends(e.target.checked)}
                  className="peer sr-only"
                />
                <div
                  className={`w-4 h-4 border-2 rounded flex items-center justify-center transition-colors
                    ${showWeekends ? "bg-[#029831] border-[#029831]" : "border-gray-300 bg-surface-default dark:bg-surface-default"}`}
                >
                  {showWeekends && (
                    <svg
                      className="w-3 h-3 text-white"
                      viewBox="0 0 12 12"
                      fill="none"
                    >
                      <path
                        d="M10 3L4.5 8.5L2 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              </div>
              <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:text-gray-100">
                Show weekends
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Collapse Button */}
      <button
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        className="absolute left-64 top-4 -mr-3 h-8 w-3 bg-surface-default dark:bg-surface-default border border-gray-200 dark:border-gray-700 rounded-r-md flex items-center justify-center"
      >
        <svg
          className={`w-4 h-4 text-gray-400 transform ${sidebarCollapsed ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>
    </div>
  );

  // Add styles for checkboxes
  const checkboxStyles = `
    .form-checkbox {
      appearance: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      width: 16px;
      height: 16px;
      border: 2px solid #d1d5db;
      border-radius: 4px;
      outline: none;
      cursor: pointer;
      position: relative;
      transition: all 0.2s ease;
    }

    .form-checkbox:checked {
      border-color: transparent;
    }

    .form-checkbox:checked::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 8px;
      height: 8px;
      border-radius: 2px;
      background-color: white;
    }

    .form-checkbox:focus {
      box-shadow: 0 0 0 2px rgba(2, 152, 49, 0.2);
    }
  `;

  return (
    <SubPageLayout
      breadcrumbItems={[
        { label: "Advisor Portal", path: "/advisor-portal" },
        { label: "Calendar", path: "/advisor-portal/calendar-events" },
      ]}
      title="Calendar"
      subtitle="Schedule and manage your appointments"
      actionButton={
        <button
          onClick={handleCreateEventClick}
          className="px-4 py-2 text-sm font-medium text-white bg-[#029831] rounded-md hover:bg-[#018529] transition-colors"
        >
          + Create Event
        </button>
      }
    >
      <div className="flex h-[calc(100vh-12rem)] bg-surface-default dark:bg-surface-default rounded-lg shadow-sm relative">
        {/* Sidebar */}
        {renderSidebar()}

        {/* Main Calendar Area */}
        <div className="flex-1 min-w-0">
          <style>
            {`
              ${checkboxStyles}
              .task-event {
                border-left: 3px solid ${EVENT_TYPES.TASK.borderColor} !important;
              }
              .task-overdue {
                background-color: #ffebee !important;
                border-color: #ef5350 !important;
              }
              .task-pending {
                background-color: ${EVENT_TYPES.TASK.color} !important;
              }
              .completed {
                opacity: 0.7;
                text-decoration: line-through;
              }
              .event-content {
                padding: 2px 4px;
              }
              .event-title {
                font-weight: 500;
                display: flex;
                justify-content: space-between;
                align-items: center;
              }
              .attendee-count {
                font-size: 0.8em;
                margin-left: 4px;
              }
              .event-time {
                font-size: 0.8em;
                color: #666;
              }
              .react-calendar {
                border: none;
                width: 100%;
              }
              .react-calendar__navigation {
                margin-bottom: 0.5em;
              }
              .react-calendar__navigation button {
                min-width: 24px;
                background: none;
                font-size: 14px;
                padding: 4px;
                color: #1a1a1a;
              }
              .react-calendar__navigation button:enabled:hover,
              .react-calendar__navigation button:enabled:focus {
                background-color: #f3f4f6;
              }
              .react-calendar__month-view__weekdays {
                font-size: 12px;
                font-weight: 500;
                text-transform: uppercase;
                color: #1a1a1a;
              }
              .react-calendar__month-view__days__day {
                font-size: 12px;
                padding: 4px;
                color: #1a1a1a;
              }
              .react-calendar__tile {
                padding: 4px;
                height: 32px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 12px;
              }
              .react-calendar__tile--now {
                background: #e6f4ea;
                color: #029831;
              }
              .react-calendar__tile--active {
                background: #029831;
                color: white;
              }
              .react-calendar__tile:enabled:hover,
              .react-calendar__tile:enabled:focus {
                background-color: #f3f4f6;
              }
            `}
          </style>

          <FullCalendar
            ref={calendarRef}
            plugins={[
              dayGridPlugin,
              timeGridPlugin,
              interactionPlugin,
              listPlugin,
              googleCalendarPlugin,
              momentTimezonePlugin,
            ]}
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
            }}
            initialView={initialView}
            editable={true}
            selectable={true}
            selectMirror={true}
            dayMaxEvents={true}
            weekends={showWeekends}
            events={allEvents}
            select={handleDateSelect}
            eventClick={handleEventClick}
            eventContent={renderEvent}
            height="100%"
          />
        </div>
      </div>

      <EventModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleModalSubmit}
        initialData={selectedEvent}
        mode={modalMode}
      />
    </SubPageLayout>
  );
};

export default Calendar;
